import React, { useState, MouseEvent } from "react";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { 
  DataGrid, 
  GridColDef, 
  GridRowsProp, 
  GridRowParams, 
  GridCallbackDetails, 
  GridRowModel,
  GridRenderCellParams 
} from "@mui/x-data-grid";
import { useStyles } from "../../assets/styles/Theme.tsx";

// Types and Interfaces
export interface MenuItemType {
  text: string;
  icon?: React.ReactNode;
  onClick: (id: string | number) => void;
}

// Update this to match GridColDef more closely
export interface ColumnDefinition {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  minWidth?: number;
  textAlign?: "left" | "center" | "right"; // Keep this for backwards compatibility
  headerAlign?: "left" | "center" | "right"; // ADD THIS
  align?: "left" | "center" | "right";       // ADD THIS
  textTransform?: "capitalize" | "uppercase" | "lowercase" | "none";
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
  renderHeader?: () => React.ReactNode;      // ADD THIS
  sortable?: boolean;
  filterable?: boolean;
  disableColumnMenu?: boolean;
  editable?: boolean; 
}

export interface ViewDataGridProps {
  columns: ColumnDefinition[];
  rows: GridRowsProp;
  menuItems?: MenuItemType[];
  tabletitle?: string;
  gridid?: string;
  displayActions?: boolean;
  displaycheckbox?: boolean;
  onRowSelect?: (row: any) => void;
  processRowUpdate?: (newRow: GridRowModel, oldRow: GridRowModel) => Promise<GridRowModel> | GridRowModel;
  onProcessRowUpdateError?: (error: Error) => void;
}

interface AnchorElState {
  anchor: HTMLElement;
  row: any;
}

// Extended theme interface
interface CustomTheme {
  palette: {
    background: {
      paper: string;
      bgShade_1?: string;
    };
    text: {
      secondary?: string;
    };
  };
  typography: {
    body_s?: {
      fontSize: string;
    };
  };
}

const ViewDataGrid: React.FC<ViewDataGridProps> = ({
  columns,
  rows,
  menuItems = [],
  tabletitle,
  gridid = "id",
  displayActions = true,
  displaycheckbox = false,
  onRowSelect,
  processRowUpdate,
  onProcessRowUpdateError,
}) => {
  const [anchorEl, setAnchorEl] = useState<AnchorElState | null>(null);
  const [density] = useState<string>("standard");

  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>, row: any): void => {
    setAnchorEl({ anchor: event.currentTarget, row });
  };

  const handleCloseUserMenu = (): void => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (menuItem: MenuItemType, row: any): void => {
    if (row && gridid) {
      menuItem.onClick(row[gridid]);
    }
    handleCloseUserMenu();
  };

  if (!Array.isArray(columns) || !Array.isArray(rows)) {
    console.error("Invalid columns or rows");
    return null;
  }

  const titleCase = (str: string = ""): string =>
    str?.replace(/\b\w/g, (char) => char.toUpperCase());

  // Use GridColDef for the action column
  const actionColumn: GridColDef[] = displayActions
    ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 90,
          align: "center",
          headerAlign: "center",
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams) => (
            <IconButton
              onClick={(event) =>
                handleOpenUserMenu(event, params.row)
              }
            >
              <MoreVertIcon />
            </IconButton>
          ),
        },
      ]
    : [];

  // Convert ColumnDefinition to GridColDef - preserve editable property
  const dataGridColumns: GridColDef[] = columns.map((column) => ({
    field: column.field,
    headerName: titleCase(column.headerName),
    width: column.width,
    flex: column.flex,
    minWidth: column.minWidth,
    // USE THE NEW PROPS, fallback to textAlign, then to "left"
    headerAlign: column.headerAlign || column.textAlign || "left", 
    align: column.align || column.textAlign || "left",
    renderHeader: column.renderHeader, // Pass the custom header renderer
    renderCell: column.renderCell,
    sortable: column.sortable,
    filterable: column.filterable,
    disableColumnMenu: column.disableColumnMenu,
    editable: column.editable || false, 
  }));

  const handleRowClick = (params: GridRowParams): void => {
    if (onRowSelect) {
      onRowSelect(params.row);
    }
  };

  const handleProcessRowUpdateError = (error: Error): void => {
    console.error("Error updating row:", error);
    if (onProcessRowUpdateError) {
      onProcessRowUpdateError(error);
    }
  };

  return (
    <Paper sx={styles.VDG_paper}>
      <Box sx={{ height: 480, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={[...dataGridColumns, ...actionColumn]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          pageSizeOptions={[10, 25, 100]}
          checkboxSelection={displaycheckbox}
          density={density as "standard" | "compact" | "comfortable"}
          disableColumnMenu
          disableRowSelectionOnClick={false}
          localeText={{ noRowsLabel: "No data found" }}
          onRowClick={handleRowClick}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          editMode="cell" // Add this to enable cell editing
          sx={{
            fontSize: theme.typography.body_s?.fontSize || '0.875rem',
            p: 1,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.paper,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              textTransform: "capitalize",
            },
          }}
        />
      </Box>

      {/* ACTION MENU */}
      <Menu
        anchorEl={anchorEl?.anchor}
        open={Boolean(anchorEl)}
        onClose={handleCloseUserMenu}
        sx={styles.VDG_colActionIconMenu}
      >
        {menuItems?.map((menuItem: MenuItemType, index: number) => (
          <MenuItem
            key={index}
            onClick={() =>
              handleMenuItemClick(menuItem, anchorEl?.row)
            }
            sx={styles.VDG_colActionIcon}
          >
            {menuItem.icon}
            <span className="menuItemText" style={{ marginLeft: '8px' }}>{menuItem.text}</span>
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};

export default ViewDataGrid;